import { useMemo, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import {
  useGetEnabledRoutes,
  useGetActiveRoute,
  useAssignRoute,
} from '../services';

import {
  EnabledRouteList,
  EnabledRouteEmptyState,
  ActiveStateRoute,
  EnabledRouteCard,
  EnabledRouteDialog,
  EnabledRouteLoader,
  RoutePickupScheduleAlert,
} from './components';

import { sortMaxOrderNewClouder } from '../../../utils/sortOrders';
import { ROUTE_TYPES } from '@wareclouds/frontend-common/b2c';
import { IS_CL_CONTEXT } from '../../../config/consts/commons';

const EMPTY_ROUTES = [];

function EnabledRoutes({
  routeType,
  isIdentityValidationBlocked = false,
  onRequireIdentityValidation,
  approvedIdentityRouteId = null,
  onApprovedIdentityRouteConsumed,
}) {
  const { t } = useTranslation(['title', 'description']);
  const [open, setOpen] = useState(false);
  const [selectedRouteId, setSelectedRouteId] = useState(null);
  const [sortedRoutes, setSortedRoutes] = useState([]);
  const isSameDay = routeType === ROUTE_TYPES.SAME_DAY;

  const { data: activeRoute, isLoading: isLoadingActiveRoute } =
    useGetActiveRoute({ routeType });

  const { data: enabledRoutes, isLoading: isLoadingEnabledRoutes } =
    useGetEnabledRoutes({ routeType });
  const routes = enabledRoutes?.data?.routes ?? EMPTY_ROUTES;
  const isNewClouderData = enabledRoutes?.data?.is_new_clouder;
  const isNewClouderFeatureEnabled =
    import.meta.env.VITE_NEW_CLOUDERS_FEATURE_ENABLED === 'true';
  const isNewClouder =
    isNewClouderData &&
    isSameDay &&
    isNewClouderFeatureEnabled &&
    IS_CL_CONTEXT;

  useEffect(() => {
    if (
      isNewClouder &&
      isSameDay &&
      isNewClouderFeatureEnabled &&
      IS_CL_CONTEXT
    ) {
      setSortedRoutes(sortMaxOrderNewClouder(routes));
    } else {
      setSortedRoutes(routes);
    }
  }, [isNewClouder, isSameDay, routes, isNewClouderFeatureEnabled]);

  const canTakeRoute = useMemo(() => {
    return activeRoute?.data?.can_take_route;
  }, [activeRoute]);

  const selectedRoute = useMemo(() => {
    return routes.find((route) => route.id_route === selectedRouteId);
  }, [routes, selectedRouteId]);

  const { isPending: loadingAssignRoute, mutateAsync } = useAssignRoute({
    routeType,
  });

  useEffect(() => {
    if (approvedIdentityRouteId == null) return;
    setSelectedRouteId(approvedIdentityRouteId);
    setOpen(true);
    onApprovedIdentityRouteConsumed?.();
  }, [approvedIdentityRouteId, onApprovedIdentityRouteConsumed]);

  const handleClose = () => {
    setOpen(false);
    setSelectedRouteId(null);
  };

  const handleTakeRoute = (route) => {
    if (isIdentityValidationBlocked) {
      onRequireIdentityValidation?.(route.id_route);
      return;
    }
    setSelectedRouteId(route);
    setOpen(true);
  };

  const handleAssignRoute = async () => {
    try {
      await mutateAsync({ id_route: selectedRouteId });
    } catch (error) {
      console.error(error);
    } finally {
      handleClose();
    }
  };

  if (isLoadingEnabledRoutes || isLoadingActiveRoute) {
    return <EnabledRouteLoader />;
  }

  return (
    <>
      <EnabledRouteList
        routes={sortedRoutes}
        canTakeRoute={canTakeRoute}
        renderActiveStateRoute={() => (
          <ActiveStateRoute routeType={routeType} />
        )}
        renderEmptyStateEnabledRoute={() => <EnabledRouteEmptyState />}
        renderEnabledRouteAlert={() => {
          if (routes.length && canTakeRoute) {
            return <RoutePickupScheduleAlert routeType={routeType} />;
          }
          return null;
        }}
      >
        {(route) => (
          <EnabledRouteCard
            key={route.id_route}
            route={route}
            handleTakeRoute={handleTakeRoute}
            canTakeRoute={canTakeRoute}
            isNewClouder={isNewClouder}
          />
        )}
      </EnabledRouteList>
      {open && (
        <EnabledRouteDialog
          open={open}
          handleClose={handleClose}
          title={t('routes.enabled', { ns: 'title', route: selectedRouteId })}
          isLoading={loadingAssignRoute}
          handleAssignRoute={handleAssignRoute}
          selectedRoute={selectedRoute}
        />
      )}
    </>
  );
}

export default EnabledRoutes;
